import { Security, useOktaAuth } from "@okta/okta-react";
import { OktaAuth } from "@okta/okta-auth-js";
import { useEffect, useState } from "react";
import Cookies from "js-cookie";
import { Container, Spinner } from "react-bootstrap";
import { userService } from "../services/user.service";
import armaninoLogo from "../assets/armanino-llp.png";
import config from "../environments/config";
import Utils from "../utils";

function OktaAuthWrapper({ children, oktaConfig }) {
  const oktaConfigured = oktaConfig && oktaConfig.issuer && oktaConfig.clientId;
  const oktaAuth = oktaConfigured ? new OktaAuth(oktaConfig) : undefined;

  const restoreOriginalUri = async (_oktaAuth, originalUri) => {
    window.location.replace(originalUri || "/");
  };

  return (
    <>
      {oktaConfigured ? (
        <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
          <OktaSubWrapper>{children}</OktaSubWrapper>
        </Security>
      ) : (
        <>{children}</>
      )}
    </>
  );
}

function OktaSubWrapper({ children }) {
  const { authState, oktaAuth } = useOktaAuth();
  const [authenticating, setAuthenticating] = useState(false);
  const [error, setError] = useState(false);
  const searchParams = new URLSearchParams(window.location.search);
  const redirectUrl = searchParams.get("returnUrl");

  useEffect(() => {
    const handleAuth = async () => {
      if (authState?.isAuthenticated && authState.accessToken) {
        const mcpOAuthStateRaw = sessionStorage.getItem("mcpOAuthState");
        if (redirectUrl || mcpOAuthStateRaw) {
          setAuthenticating(true);
        }

        const userInfo = await oktaAuth.getUser();
        const keepMeSignedIn =
          localStorage.getItem("keepMeSignedIn") === "true";

        const user = {
          firstName: userInfo.given_name,
          lastName: userInfo.family_name,
          email: userInfo.email,
          keepMeSignedIn,
        };

        userService.oktaLogin(user, authState.accessToken.accessToken).then(
          (response) => {
            const token = response.token;
            const payload = JSON.parse(atob(token.split(".")[1]));
            const expirationDate = new Date(payload.exp * 1000);

            // Normalize clientId: use empty string instead of null/undefined for consistency
            const userData = response;
            if (userData.clientId === null || userData.clientId === undefined) {
              userData.clientId = "";
            }
            if (userData.clientGuid === null || userData.clientGuid === undefined) {
              userData.clientGuid = "";
            }
            if (userData.clientName === null || userData.clientName === undefined) {
              userData.clientName = "";
            }

            // Convert string boolean values to actual booleans
            // Backend returns "True"/"False" strings instead of true/false booleans
            if (typeof userData.isAdmin === 'string') {
              userData.isAdmin = userData.isAdmin.toLowerCase() === 'true';
            }
            if (typeof userData.isSuperUser === 'string') {
              userData.isSuperUser = userData.isSuperUser.toLowerCase() === 'true';
            }
            if (typeof userData.isProductTeam === 'string') {
              userData.isProductTeam = userData.isProductTeam.toLowerCase() === 'true';
            }

            Cookies.set("user", JSON.stringify(userData), {
              expires: expirationDate,
            });

            if (mcpOAuthStateRaw) {
              sessionStorage.removeItem("mcpOAuthState");
              try {
                const { returnTo, mcpState } = JSON.parse(mcpOAuthStateRaw);
                if (returnTo && Utils.validateReturnToUrl(returnTo, config.mcpAllowedReturnToPrefixes)) {
                  userService.getMcpRedirectUrl(returnTo, mcpState).then(
                    (mcpResponse) => { window.location.href = mcpResponse.data.redirectUrl; },
                    () => { window.location.href = redirectUrl || Utils.getHomeRoute(); }
                  );
                  return;
                }
              } catch { /* fall through to normal redirect */ }
            }
            if (redirectUrl) {
              window.location.href = redirectUrl;
            }
          },
          () => {
            setError(true);
          }
        );
      }
    };

    if (!Cookies.get("user")) handleAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState, oktaAuth]);

  return (
    <>
      {!authenticating ? (
        <>{children}</>
      ) : (
        <div className="centered-div">
          <Container className="login-box bg-white p-5 rounded border border-secondary mt-5">
            <div className="text-center mb-5">
              <img
                src={armaninoLogo}
                alt="Armanino"
                width={250}
              />

              <div className="mt-5">
                {!error ? (
                  <>
                    Authenticating <Spinner className="ms-3" size="sm" />
                  </>
                ) : (
                  <span className="text-danger">
                    An error has occurred and we can not authenticate your email
                    right now.
                  </span>
                )}
              </div>
            </div>
          </Container>
        </div>
      )}
    </>
  );
}

export default OktaAuthWrapper;
